import { AttemptGrade } from '../../minigames/MiniGame';

/** Drawing helpers shared by the mini-game painters (2D canvas, CSS pixels – the overlay scales for the DPR). */

export const COLORS = {
  navy: '#172441',
  orange: '#f59f0a',
  gold: '#ffd25a',
  green: '#2fb75b',
  red: '#e0473c',
  white: '#ffffff',
} as const;

/** Small deterministic random (mulberry32) – the same sparks and grain every frame. */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
export const easeIn = (t: number): number => t * t * t;
export const easeOut = (t: number): number => 1 - (1 - t) ** 3;
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

export function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/** Brushed steel across a box (left dark, highlight off centre, right dark). */
export function steel(ctx: CanvasRenderingContext2D, x0: number, x1: number, tint = 0): CanvasGradient {
  const g = ctx.createLinearGradient(x0, 0, x1, 0);
  const c = (l: number) => `hsl(${210 + tint} 8% ${l}%)`;
  g.addColorStop(0, c(28));
  g.addColorStop(0.28, c(78));
  g.addColorStop(0.42, c(92));
  g.addColorStop(0.6, c(58));
  g.addColorStop(1, c(24));
  return g;
}

/** Soft elliptic shadow (radial gradient – ctx.filter blur is missing in Safari). */
export function softShadow(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, alpha: number): void {
  if (alpha <= 0 || rx <= 0 || ry <= 0) return;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, ry / rx);
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx);
  g.addColorStop(0, `rgba(0,0,0,${alpha})`);
  g.addColorStop(0.55, `rgba(0,0,0,${alpha * 0.55})`);
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, rx, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Offscreen picture drawn once per size (backgrounds, wood, sky) – cheap to blit every frame. */
export class Cached {
  private canvas: HTMLCanvasElement | null = null;
  private key = '';

  get(w: number, h: number, scale: number, draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void): HTMLCanvasElement {
    const key = `${w}x${h}@${scale}`;
    if (!this.canvas || key !== this.key) {
      this.canvas ??= document.createElement('canvas');
      this.canvas.width = Math.max(1, Math.round(w * scale));
      this.canvas.height = Math.max(1, Math.round(h * scale));
      const ctx = this.canvas.getContext('2d')!;
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      draw(ctx, w, h);
      this.key = key;
    }
    return this.canvas;
  }
}

/** Screen shake after an impact: a quick decaying jolt. */
export function shakeOffset(sinceImpact: number, strength: number, t: number): { x: number; y: number } {
  if (sinceImpact > 0.35) return { x: 0, y: 0 };
  const k = Math.exp(-sinceImpact * 14) * strength;
  return { x: Math.sin(t * 97) * k, y: Math.cos(t * 83) * k * 0.7 };
}

export interface Burst {
  readonly x: number;
  readonly y: number;
  readonly count: number;
  readonly speed: number;
  readonly gravity: number;
  readonly life: number;
  readonly colors: readonly string[];
  /** Spread around straight up (rad); Math.PI = all around. */
  readonly spread: number;
  readonly size: number;
  readonly seed: number;
  /** Streaks (sparks) instead of chunks (chips, dust). */
  readonly streaks: boolean;
}

/** Particles of a burst at `age` seconds – computed, not simulated (the same every frame). */
export function drawBurst(ctx: CanvasRenderingContext2D, b: Burst, age: number): void {
  if (age < 0 || age > b.life) return;
  const rnd = seeded(b.seed);
  ctx.save();
  ctx.lineCap = 'round';
  for (let i = 0; i < b.count; i++) {
    const a = -Math.PI / 2 + (rnd() * 2 - 1) * b.spread;
    const v = b.speed * (0.45 + rnd() * 0.75);
    const life = b.life * (0.55 + rnd() * 0.45);
    const color = b.colors[Math.floor(rnd() * b.colors.length)]!;
    const size = b.size * (0.6 + rnd() * 0.8);
    if (age > life) continue;
    const vx = Math.cos(a) * v;
    const vy = Math.sin(a) * v;
    const x = b.x + vx * age;
    const y = b.y + vy * age + 0.5 * b.gravity * age * age;
    const fade = 1 - age / life;
    ctx.globalAlpha = fade;
    if (b.streaks) {
      const dt = 0.035;
      const px = x - vx * dt;
      const py = y - (vy + b.gravity * age) * dt;
      ctx.strokeStyle = color;
      ctx.lineWidth = size;
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(x, y);
      ctx.stroke();
    } else {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.ellipse(x, y, size, size * 0.7, a + age * 6, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

const GRADE_TEXT: Readonly<Record<AttemptGrade, string>> = {
  [AttemptGrade.Perfect]: 'PERFEKT!',
  [AttemptGrade.Good]: 'GUT',
  [AttemptGrade.Miss]: 'DANEBEN',
};
const GRADE_COLOR: Readonly<Record<AttemptGrade, string>> = {
  [AttemptGrade.Perfect]: COLORS.gold,
  [AttemptGrade.Good]: '#8fe39f',
  [AttemptGrade.Miss]: '#ff7a6b',
};

/** The judged attempt as a big popping word that floats up and fades. `text` overrides the default word. */
export function drawGradeLabel(ctx: CanvasRenderingContext2D, grade: AttemptGrade | null, age: number, x: number, y: number, size: number, text?: string): void {
  if (grade === null || age > 1.15) return;
  const pop = age < 0.12 ? easeOut(age / 0.12) * 1.25 : 1.25 - 0.25 * clamp01((age - 0.12) / 0.15);
  const alpha = age < 0.8 ? 1 : 1 - (age - 0.8) / 0.35;
  ctx.save();
  ctx.translate(x, y - age * size * 0.6);
  ctx.scale(pop, pop);
  ctx.globalAlpha = clamp01(alpha);
  ctx.font = `900 italic ${size}px "Segoe UI", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.lineWidth = size * 0.16;
  ctx.strokeStyle = 'rgba(10, 16, 32, 0.85)';
  const word = text ?? GRADE_TEXT[grade];
  ctx.strokeText(word, 0, 0);
  ctx.fillStyle = GRADE_COLOR[grade];
  ctx.fillText(word, 0, 0);
  ctx.restore();
}

/** Darkened edges so the scene sits in the panel. */
export function vignette(ctx: CanvasRenderingContext2D, w: number, h: number, strength = 0.45): void {
  const g = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.max(w, h) * 0.75);
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(1, `rgba(0,0,0,${strength})`);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}
